<template>
    <div class="flex items-start gap-2 p-4 border border-slate-200 rounded-lg">
        <div class="flex-1" :data-test="`header-name-wrapper-${props.index}`">
            <vx-label size="lg">Name</vx-label>
            <vx-input size="lg"
                :data-test="`header-name-${props.index}`"
                autocomplete="off"
                data-1p-ignore
                data-lpignore="true"
                :readonly="props.autofillLock"
                v-model="header.name"
                @focus="emit('focus')"
                :error="displayFirstError(validation.name.$errors)"></vx-input>
        </div>

        <div class="flex-1" :data-test="`header-value-wrapper-${props.index}`">
            <vx-label size="lg">Value</vx-label>
            <vx-input size="lg"
                :data-test="`header-value-${props.index}`"
                :type="showSecret ? 'text' : 'password'"
                autocomplete="off"
                data-1p-ignore
                data-lpignore="true"
                :readonly="props.autofillLock"
                v-model="header.value"
                :placeholder="props.authConfigId ? '•••••••••' : ''"
                @focus="onValueFocus"
                @blur="showSecret = false"
                :error="displayFirstError(validation.value.$errors)"></vx-input>
        </div>

        <div>
            <vx-label size="lg" class="invisible" aria-hidden="true">Remove</vx-label>
            <vx-button type="button"
                size="lg"
                color="muted"
                :disabled="!props.removable"
                @click="emit('remove')"
                aria-label="Remove header">
                <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxInput, VxLabel } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed, nextTick, ref } from 'vue';
import { displayFirstError } from '~/utils/validation';

const props = defineProps({
    index: {
        type: Number,
        required: true,
    },
    headers: {
        type: Array,
        required: true,
    },
    authConfigId: {
        type: String,
        default: undefined,
    },
    removable: {
        type: Boolean,
        required: true,
    },
    autofillLock: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits(['remove', 'focus']);

const header = defineModel({ required: true });

const showSecret = ref(false);

const onValueFocus = async () => {
    emit('focus');
    await nextTick();
    showSecret.value = true;
};

// Mirrors the backend HeaderListValidator RESERVED_NAMES — keep in sync.
const RESERVED_NAMES = new Set([
    'content-type',
    'content-length',
    'host',
    'connection',
    'transfer-encoding',
    'content-encoding',
    'te',
    'trailer',
    'upgrade',
    'keep-alive',
    'proxy-connection',
    'content-digest',
    'user-agent',
    'date',
    'signature',
    'signature-input',
    'forwarded',
    'x-forwarded-for',
    'x-forwarded-host',
    'x-forwarded-proto',
    'x-real-ip',
    'via',
    'proxy-authorization',
    'proxy-authenticate',
    'expect',
    'max-forwards',
    'cookie',
]);

const NAME_REGEX = /^[a-zA-Z0-9_-]{1,40}$/;
// Mirrors the backend HeaderListValidator regex character-for-character — keep in sync.
// eslint-disable-next-line no-useless-escape
const VALUE_REGEX = /^[a-zA-Z0-9_ \:;\.,\\\/\"\'\?\!\(\)\{\}\[\]@\<\>\=\-\+\*\#\$&`\|~\^%]{1,4095}$/;

const requiredName = helpers.withMessage('Name is required', required);
const requiredValue = helpers.withMessage('Value is required', required);

const validNameFormat = helpers.withMessage(
    'Only letters, numbers, underscores, and hyphens are allowed (max 40 chars).',
    (v) => typeof v !== 'string' || v.trim() === '' || NAME_REGEX.test(v.trim()),
);

const notReservedName = helpers.withMessage(
    'This header name is reserved and cannot be used.',
    (v) => {
        if (typeof v !== 'string') { return true; }
        const normalized = v.trim().toLowerCase();
        if (normalized === '') { return true; }
        if (RESERVED_NAMES.has(normalized)) { return false; }
        if (normalized.startsWith('x-voxie-')) { return false; }
        return true;
    },
);

const validValueFormat = helpers.withMessage(
    'Contains disallowed characters or exceeds 4095 characters.',
    (v) => typeof v !== 'string' || v === '' || VALUE_REGEX.test(v.trim()),
);

const noDuplicateNames = helpers.withMessage(
    'Duplicate header name.',
    (v) => {
        if (typeof v !== 'string') { return true; }
        const normalized = v.trim().toLowerCase();
        if (normalized === '') { return true; }
        return !props.headers.some(
            (h, i) => i !== props.index && h.name.trim().toLowerCase() === normalized,
        );
    },
);

const validation = useVuelidate(
    computed(() => ({
        name: { requiredName, validNameFormat, notReservedName, noDuplicateNames },
        value: { requiredValue, validValueFormat },
    })),
    computed(() => ({
        name: header.value.name,
        value: header.value.value,
    })),
    { $autoDirty: true },
);
</script>
